import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, X, DollarSign } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const METHODS = [
  { id: "cash", label: "Cash", hint: "Collected in person. Sends receipt if notified." },
  { id: "check", label: "Check", hint: "Enter the check number in Reference. Sends receipt if notified." },
  { id: "card", label: "Credit Card", hint: "Paid via Stripe. Use for reconciling charges that didn't auto-mark paid (missed webhook, orphaned session). Reference = Stripe charge ID or last-4." },
  { id: "po", label: "Purchase Order", hint: "Silent — no customer email or SMS. Bookkeeping only." },
];

const referenceHint = (m) =>
  m === "check"
    ? "Check #"
    : m === "card"
    ? "Stripe charge / last-4 (optional)"
    : m === "cash"
    ? "Receipt # (optional)"
    : "PO # (optional)";

const defaultMethodFor = (order) => {
  const t = (order?.type || "").toLowerCase();
  if (t === "agency_po") return "po";
  if (t === "stripe") return "card";
  if (t === "cash") return "cash";
  return "cash";
};

/**
 * Modal for admin to mark an unpaid order as paid.
 * Payment methods: cash / check / purchase order.
 *   • cash + check → optional customer receipt email + internal SMS
 *   • purchase order → silent bookkeeping only (checkbox hidden)
 */
export default function MarkAsPaidModal({ order, onClose, onSaved }) {
  const [method, setMethod] = useState(defaultMethodFor(order));
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setMethod(defaultMethodFor(order));
    setReference("");
    setNote("");
    setNotify(true);
    setError("");
    // Intentional: reset only when a different order is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  useEffect(() => {
    // PO settlements never notify. Card defaults notify OFF because Stripe
    // already emailed a receipt at capture — admin can still opt in for
    // the branded FFF "we've reconciled your payment" note.
    if (method === "po") setNotify(false);
    else if (method === "card") setNotify(false);
    else setNotify(true);
  }, [method]);

  if (!order) return null;
  const total = Number(order.total || 0);
  const isPO = method === "po";
  const customerLabel =
    order.customer_name || order.customer_email || order.user_email || order.id;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`/admin/orders/${order.id}/mark-paid`, {
        payment_method: method,
        reference: reference.trim() || null,
        note: note.trim() || null,
        notify_customer: !isPO && notify,
      });
      onSaved?.({ payment_method: method, notify: !isPO && notify });
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) ||
        err?.message ||
        "Could not mark as paid.";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="mark-as-paid-modal"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-black/5">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-green/15 text-brand-green shrink-0">
              <DollarSign className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="font-display font-black text-[18px] leading-tight text-brand-charcoal truncate">
                Mark as Paid
              </p>
              <p className="text-[12px] text-brand-charcoal/55 truncate">
                {customerLabel} · ${total.toFixed(2)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-brand-charcoal/60 hover:bg-brand-charcoal/5 hover:text-brand-charcoal transition-colors"
            data-testid="mark-as-paid-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
          {/* Method picker */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-2">
              Payment method
            </p>
            <div className="grid grid-cols-1 gap-2">
              {METHODS.map((m) => {
                const active = method === m.id;
                return (
                  <label
                    key={m.id}
                    data-testid={`mark-as-paid-method-${m.id}`}
                    className={`flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-colors ${
                      active
                        ? "border-brand-teal bg-brand-teal/5"
                        : "border-black/10 hover:border-brand-teal/40"
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value={m.id}
                      checked={active}
                      onChange={() => setMethod(m.id)}
                      className="mt-1 h-4 w-4 accent-brand-teal"
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block font-bold text-[14px] text-brand-charcoal">
                        {m.label}
                      </span>
                      <span className="block text-[12px] text-brand-charcoal/60 mt-0.5">
                        {m.hint}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Reference */}
          <div>
            <label
              htmlFor="mark-paid-reference"
              className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 block mb-1.5"
            >
              {referenceHint(method)}
            </label>
            <input
              id="mark-paid-reference"
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              data-testid="mark-as-paid-reference"
              placeholder={
                method === "check"
                  ? "e.g. 1042"
                  : method === "card"
                  ? "e.g. ch_3O… or •••• 4242"
                  : "Optional"
              }
              maxLength={120}
              className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-[14px] text-brand-charcoal focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal"
            />
          </div>

          {/* Internal note */}
          <div>
            <label
              htmlFor="mark-paid-note"
              className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 block mb-1.5"
            >
              Internal note (optional)
            </label>
            <textarea
              id="mark-paid-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="mark-as-paid-note"
              rows={3}
              maxLength={1000}
              placeholder="Anything to remember for the books…"
              className="w-full rounded-xl border border-black/10 px-3 py-2.5 text-[14px] text-brand-charcoal focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal resize-none"
            />
          </div>

          {/* Notify customer — hidden for PO */}
          {!isPO && (
            <label className="flex items-start gap-3 rounded-2xl bg-brand-cream/50 border border-brand-cream p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                data-testid="mark-as-paid-notify"
                className="mt-0.5 h-4 w-4 accent-brand-teal"
              />
              <span className="flex-1 min-w-0">
                <span className="block font-bold text-[13px] text-brand-charcoal">
                  Email a receipt to {order.customer_email || "the customer"}
                </span>
                <span className="block text-[12px] text-brand-charcoal/60 mt-0.5">
                  Sends a “Payment received” confirmation with the method + reference. An internal SMS goes out either way.
                </span>
              </span>
            </label>
          )}

          {error && (
            <div
              data-testid="mark-as-paid-error"
              className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-[13px]"
            >
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-black/5 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            data-testid="mark-as-paid-cancel"
            className="rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em] hover:border-brand-charcoal/40 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            data-testid="mark-as-paid-submit"
            className="inline-flex items-center gap-2 rounded-full bg-brand-green text-white px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em] hover:bg-brand-green/90 transition-colors disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4" />
            )}
            {saving ? "Marking…" : "Mark Paid"}
          </button>
        </div>
      </div>
    </div>
  );
}
